import {
  objectTypes,
  ObjectTypes,
  getRoomIconFromType,
  NAME_VALIDATION_DEBOUNCE_TIME,
  getIconFromProductType,
} from '@/core/utils/utils';
import { PropType } from 'vue';
import { mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import { Vue, Options } from 'vue-class-component';
import {
  EnergyMeterAndLoadControlType,
  RoomDTO,
  RoomObjectType,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import { FETCH_PLANT } from '@/core/utils/emitter-events';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { NamingService } from '@/core/services/naming.service';
import { PlantEntityType } from '@/../client-generator/generated-clients/configurations';

@Options({
  name: 'PlantDashboardRoom',
  emits: ['add-object'],
  props: {
    room: {
      type: Object as PropType<RoomDTO>,
      required: false,
    },
    plantId: {
      type: Number,
      required: true,
    },
  },
  watch: {
    room: function () {
      this.updateCounts();
    },
    'room.name': function () {
      this.verifyValidityOfName();
    },
  },
  computed: {
    ...mapGetters('UI', ['plantInlineRenameEnabled']),
  },
})
export default class PlantDashboardRoom extends Vue {
  getRoomIconFromType = getRoomIconFromType;
  RoomObjectType = RoomObjectType;
  plantInlineRenameEnabled!: boolean;
  room!: RoomDTO;
  plantId!: number;
  objectsCounts: {[key: string]: number} = {};
  objectTypes: ObjectTypes[] = objectTypes;

  isValidName = true;
  initialName!: string;
  invalidNameReason: string = '';

  get roomObjects(): {name: string, icon: string, type: RoomObjectType, count: number}[] {
    const roomObjects: {name: string, icon: string, type: RoomObjectType, count: number}[] = [];
    for (const key in this.objectsCounts) {
      const type = key.split('-')[0];
      const loadControlType = Number(key.split('-')[1]);
      const objectType = this.objectTypes.find((o) => o.type.toString() === type);
      if (objectType) {
        const icon = loadControlType ? getIconFromProductType(loadControlType) : objectType.icon;
        const count = this.objectsCounts[key];
        roomObjects.push({ name: objectType.name, icon, type: objectType.type, count });
      }
    }
    return roomObjects;
  }

  get roomObjectCount() {
    let count = 0;
    count = this.room.roomObjects?.length ?? 0;
    return count;
  }

  async created() {
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME + 1000
    );
  }

  mounted() {
    this.updateCounts();
    this.initialName = this.room.name!;
  }

  private async verifyValidityOfName() {
    if (this.room.name === this.initialName) return;
    const validName = NamingService.isValidEntityTypeName(this.room.name);

    if (this.initialName !== this.room.name && !validName) {
      this.$store.dispatch(SET_APP_LOADING, true);
      const suggestedName = await NamingService.suggestNextValidEntityName({
        eType: PlantEntityType.ROOM,
        plantId: this.plantId,
        metadata: {
          roomType: this.room.type,
        },
      });
      this.isValidName = validName;
      this.invalidNameReason = this.$t(
        `uniqueNaming.validationErrorWithSuggestion`,
        { suggestedName }
      );
      this.$store.dispatch(SET_APP_LOADING, false);
    } else {
      this.isValidName = true;
      this.invalidNameReason = '';
      await this.updateRoomName();
    }
  }

  private async updateRoomName() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      await plantsClient.roomsPUT(this.plantId, this.room.id!, this.room);
      this.$toast.add({
        severity: 'success',
        summary: this.$t('uniqueNaming.roomInlineNamingSuccess'),
        life: 3000,
      });
      NamingService.forcefullyRenameFirstEntityWithinCachedOnes(
        this.initialName,
        this.room.name!
      );
      this.initialName = this.room.name!;
    } catch (e) {
      this.room.name = this.initialName;
      this.$toast.add({
        severity: 'error',
        summary: this.$t('uniqueNaming.roomInlineNamingError'),
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emitter.emit(FETCH_PLANT);
    }
  }

  onHideNameValidationDialog() {
    this.room.name = this.initialName;
    this.isValidName = true;
  }

  updateCounts() {
    if (!this.room.roomObjects) return;

    this.objectsCounts = this.room.roomObjects.reduce((p: any, c) => {
      var type = c.type!.toString();
      if (c.type === RoomObjectType.ActuactorLoadControl) {
        type += '-' + c.energyMeterAndLoadControlType!.toString();
      }
      if (!Object.prototype.hasOwnProperty.call(p, type)) {
        p[type] = 0;
      }
      p[type]++;
      return p;
    }, {});
  }

  async handleAddObject() {
    this.$store.dispatch(SET_APP_LOADING, true);
    await NamingService.prefetchFlatEntities(
      this.plantId,
      PlantEntityType.ROOM_OBJECT
    );
    this.$store.dispatch(SET_APP_LOADING, false);
    this.$emit('add-object');
  }

  goToRoomDetail() {
    this.$router.push({
      name: 'page-plant-room',
      params: {
        roomId: this.room.id ?? '',
      },
    });
  }
}
